import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAssetTypes } from '../../api/queries'
import { useComplianceRules } from '../../hooks/useComplianceApi'
import { useContractTypes, useDocumentTypes } from '../../hooks/useQueries'
import { subtypeLabel, useTicketSubtypes } from '../../hooks/useTicketSubtypes'
import { labelIn, type RemediationScenario } from '../../hooks/useScenariosApi'
import { UNIVERSES } from '../business-rules/rule-catalog'
import type { CodeOption } from './CodeMultiPicker'

/** A condition worth saving: anything but an empty group. */
export function hasCondition(when: unknown): boolean {
    if (!when || typeof when !== 'object') return false
    const w = when as { all?: unknown[]; any?: unknown[] }
    if (Array.isArray(w.all)) return w.all.length > 0
    if (Array.isArray(w.any)) return w.any.length > 0
    return Object.keys(w).length > 0
}

export const RULE_CLASSES = ['regulatory', 'periodic_inspection', 'management'] as const

/** The vocabularies the scenario editor's pickers offer. */
export type EditorOptions = {
    universes: CodeOption[]
    subtypes: CodeOption[]
    rules: CodeOption[]
    ruleClasses: CodeOption[]
    assetTypes: CodeOption[]
    documentTypes: CodeOption[]
    contractTypes: CodeOption[]
    /** Rule uid by code, to link a due source to its rule. */
    ruleUids: Record<string, string>
}

type Named = { code?: string | null; name?: unknown; label?: unknown; uid?: string }

function nameOf(v: unknown, lang: string, code: string): string {
    if (typeof v === 'string' && v) return v
    if (v && typeof v === 'object') return labelIn(v as Record<string, string>, lang, code)
    return code
}

function toOptions(rows: Named[] | undefined, lang: string): CodeOption[] {
    const out: CodeOption[] = []
    const seen = new Set<string>()
    for (const r of rows ?? []) {
        if (!r.code || seen.has(r.code)) continue
        seen.add(r.code)
        out.push({ code: r.code, label: nameOf(r.label ?? r.name, lang, r.code) })
    }
    return out.sort((a, b) => a.label.localeCompare(b.label))
}

/** Loads the editor's vocabularies: subtypes, rules, asset, document and contract types. */
export function useEditorOptions(org: string, scenarios: RemediationScenario[]): EditorOptions {
    const { t, i18n } = useTranslation('scenarios')
    const lang = i18n.language
    const { list: subtypes } = useTicketSubtypes(org)
    const { data: rules } = useComplianceRules(org)
    const { data: assetTypes } = useAssetTypes(org)
    const { data: documentTypes } = useDocumentTypes(org)
    const { data: contractTypes } = useContractTypes(org)

    return useMemo(() => {
        const universes = new Set<string>(UNIVERSES)
        for (const s of scenarios) for (const u of s.universes ?? []) universes.add(u)
        const ruleUids: Record<string, string> = {}
        for (const r of rules ?? []) if (r.code && !ruleUids[r.code]) ruleUids[r.code] = r.uid
        return {
            universes: [...universes].map((u) => ({ code: u, label: t(`compliance:business_rules.universe.${u}`, { defaultValue: u }) })),
            subtypes: subtypes.map((s) => ({ code: s.code, label: subtypeLabel(s, lang) || s.code })).sort((a, b) => a.label.localeCompare(b.label)),
            rules: toOptions(rules as Named[] | undefined, lang),
            ruleClasses: RULE_CLASSES.map((c) => ({ code: c, label: t(`editor.rule_class.${c}`) })),
            assetTypes: toOptions(assetTypes as Named[] | undefined, lang),
            documentTypes: toOptions(documentTypes as Named[] | undefined, lang),
            contractTypes: toOptions(contractTypes as Named[] | undefined, lang),
            ruleUids,
        }
    }, [scenarios, subtypes, rules, assetTypes, documentTypes, contractTypes, lang, t])
}
